import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./SupportChatbot.css";

const STORAGE_KEY_PREFIX = "dm_ai_chat_state";

const ACTION_ROUTE_MAP = {
  OPEN_TRANSACTIONS: "/dashboard",
  CREATE_DISPUTE: "/support",
  RETRY_PAYMENT: "/dashboard",
  OPEN_SUBSCRIPTIONS: "/subscription",
  OPEN_BULK_REGISTRATION: "/dashboard?tab=bulk",
  OPEN_ADMIN_DASHBOARD: "/dashboard",
  SUPPORT_TICKET_REDIRECTION: "/support",
  OPEN_NGO_DASHBOARD: "/dashboard",
  OPEN_PROFILE: "/dashboard",
  UPLOAD_DOCUMENTS: "/dashboard",
  VERIFY_EMAIL: "/dashboard",
};

const getApiRoot = () => {
  const base = process.env.REACT_APP_LOCAL_API || "";
  return base.replace(/\/auth\/v1\/?$/, "");
};

const getStorageKey = (userToken) => {
  if (!userToken) return `${STORAGE_KEY_PREFIX}:guest`;
  // Token-tail based key isolates chat state per logged-in account.
  return `${STORAGE_KEY_PREFIX}:${userToken.slice(-24)}`;
};

const getInitialState = (storageKey) => {
  try {
    const saved = sessionStorage.getItem(storageKey);
    if (!saved) return { conversationId: "", messages: [] };
    const parsed = JSON.parse(saved);
    return {
      conversationId: parsed.conversationId || "",
      messages: Array.isArray(parsed.messages) ? parsed.messages : [],
    };
  } catch (error) {
    return { conversationId: "", messages: [] };
  }
};

const SupportChatbot = () => {
  const userToken = localStorage.getItem("userToken");
  const userRole = localStorage.getItem("userRole") || "donor";
  const fabLabel = userRole === "admin" ? "Ask Assistant" : "Help AI";
  const storageKey = useMemo(() => getStorageKey(userToken), [userToken]);
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [chatState, setChatState] = useState(() => getInitialState(storageKey));

  useEffect(() => {
    setChatState(getInitialState(storageKey));
  }, [storageKey]);

  const quickPrompts = useMemo(() => {
    if (userRole === "admin") {
      return ["Show user activity summary", "How do I triage disputes?"];
    }
    if (userRole === "ngo" || userRole === "impactOrg") {
      return ["Why am I unverified?", "Show my recent transactions"];
    }
    return ["My payment is pending", "Show my subscription status"];
  }, [userRole]);

  const persistState = (nextState) => {
    setChatState(nextState);
    sessionStorage.setItem(storageKey, JSON.stringify(nextState));
  };

  const handleAction = (action) => {
    const route = ACTION_ROUTE_MAP[action?.type];
    if (route) {
      window.location.href = route;
    }
  };

  const clearChat = () => {
    const nextState = { conversationId: "", messages: [] };
    persistState(nextState);
  };

  const sendMessage = async (rawMessage) => {
    const message = (rawMessage || "").trim();
    if (!message || isSending) return;

    if (!userToken) {
      persistState({
        conversationId: chatState.conversationId,
        messages: [
          ...chatState.messages,
          { sender: "user", text: message, actions: [] },
          {
            sender: "bot",
            text: "Please log in to use account-specific support.",
            actions: [],
          },
        ],
      });
      setInput("");
      return;
    }

    const baseMessages = [...chatState.messages, { sender: "user", text: message, actions: [] }];
    persistState({
      conversationId: chatState.conversationId,
      messages: baseMessages,
    });
    setInput("");
    setIsSending(true);

    try {
      const response = await axios.post(
        `${getApiRoot()}/api/v1/ai/chat`,
        {
          message,
          conversationId: chatState.conversationId || undefined,
          pageContext: window.location.pathname,
        },
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        }
      );

      const reply = response?.data?.reply || "I could not generate a response.";
      const actions = response?.data?.actions || [];
      const nextConversationId = response?.data?.conversationId || chatState.conversationId;

      const nextState = {
        conversationId: nextConversationId,
        messages: [
          ...baseMessages,
          { sender: "bot", text: reply, actions },
        ],
      };
      persistState(nextState);
    } catch (error) {
      persistState({
        conversationId: chatState.conversationId,
        messages: [
          ...baseMessages,
          {
            sender: "bot",
            text: "I’m unable to reach support right now. Please try again shortly.",
            actions: [],
          },
        ],
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <button className="dm-help-fab" onClick={() => setIsOpen((prev) => !prev)}>
        {fabLabel}
      </button>

      {isOpen && (
        <div className="dm-help-overlay" onClick={() => setIsOpen(false)}>
          <div className="dm-help-drawer" onClick={(e) => e.stopPropagation()}>
          <div className="dm-help-header">
            <div className="dm-help-header-text">
              <span className="dm-help-badge">AI SUPPORT</span>
              <h3>Donor Money Assistant</h3>
              <p>Secure role-based support</p>
            </div>
            <div className="dm-help-header-actions">
              <button
                type="button"
                className="dm-help-header-btn ghost-btn"
                onClick={clearChat}
              >
                Clear
              </button>
              <button
                type="button"
                className="dm-help-header-btn close-btn"
                onClick={() => setIsOpen(false)}
              >
                x
              </button>
            </div>
          </div>

          <div className="dm-help-prompts">
            {quickPrompts.map((prompt) => (
              <button
                key={prompt}
                className="dm-help-prompt-btn"
                onClick={() => sendMessage(prompt)}
              >
                {prompt}
              </button>
            ))}
          </div>

          <div className="dm-help-messages">
            {chatState.messages.length === 0 && (
              <div className="dm-help-empty">
                Ask about payments, verification, subscriptions, and support actions.
              </div>
            )}

            {chatState.messages.map((msg, idx) => (
              <div
                key={`${msg.sender}-${idx}`}
                className={`dm-help-message ${
                  msg.sender === "user" ? "user-message" : "bot-message"
                }`}
              >
                <div style={{ whiteSpace: "pre-wrap" }}>
                  {typeof msg.text === "string" 
                    ? msg.text.split(/(\*\*.*?\*\*)/g).map((part, i) => {
                        if (part.startsWith("**") && part.endsWith("**")) {
                          return <strong key={i}>{part.slice(2, -2)}</strong>;
                        }
                        return part;
                      })
                    : msg.text
                  }
                </div>
                {Array.isArray(msg.actions) && msg.actions.length > 0 && (
                  <div className="dm-help-actions">
                    {msg.actions.map((action) => (
                      <button
                        key={`${idx}-${action.type}`}
                        onClick={() => handleAction(action)}
                      >
                        {action.label || action.type}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="dm-help-input-row">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your support question..."
              onKeyDown={(e) => {
                if (e.key === "Enter") sendMessage(input);
              }}
            />
            <button onClick={() => sendMessage(input)} disabled={isSending}>
              {isSending ? "..." : "Send"}
            </button>
          </div>
        </div>
        </div>
      )}
    </>
  );
};

export default SupportChatbot;
